import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import axios from 'axios';
import { Mic, ExternalLink } from 'lucide-react';
import { toPodcastEmbed } from '../utils/podcastEmbed';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Podcast = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [podcast, setPodcast] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchPodcast = async () => {
      try {
        const { data } = await axios.get(`${API}/podcast`);
        setPodcast(data);
      } catch (err) {
        console.error('Error fetching podcast:', err);
      } finally {
        setLoaded(true);
      }
    };
    fetchPodcast();
  }, []);

  // Don't render the section if there's no podcast configured or it's disabled
  if (!loaded || !podcast || !podcast.enabled || !podcast.embed_url) {
    return null;
  }

  const coverUrl = podcast.cover_path ? `${API}/files/${podcast.cover_path}` : null;
  const { embed, listen, platform } = toPodcastEmbed(podcast.embed_url);

  return (
    <section id="podcast" className="py-24 bg-gradient-to-b from-white to-[#E8F5FA] relative overflow-hidden">
      <div ref={ref} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <span className="text-[#F5A6A4] font-semibold text-sm uppercase tracking-wider flex items-center justify-center gap-2">
            <Mic className="w-4 h-4" /> Podcast
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-[#4A4A4A] mt-4 mb-6">
            {podcast.title}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mx-auto" />
        </motion.div>

        <div className="grid md:grid-cols-5 gap-8 items-center">
          {/* Cover */}
          {coverUrl && (
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="md:col-span-2"
            >
              <div className="relative aspect-square rounded-3xl overflow-hidden shadow-2xl">
                <img src={coverUrl} alt={podcast.title} className="w-full h-full object-cover" />
              </div>
            </motion.div>
          )}

          {/* Embed + description */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.15 }}
            className={coverUrl ? 'md:col-span-3' : 'md:col-span-5'}
          >
            {podcast.description && (
              <p className="text-lg text-gray-600 leading-relaxed mb-6">{podcast.description}</p>
            )}
            <div className="rounded-2xl overflow-hidden shadow-lg bg-white" data-testid="podcast-embed">
              <iframe
                title={podcast.title}
                src={embed}
                width="100%"
                height="232"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                style={{ borderRadius: '16px' }}
              />
            </div>
            <a
              href={listen}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="podcast-listen-link"
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#4A4A4A] hover:text-[#1DB954] transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              {platform ? `Escuchar en ${platform}` : 'Abrir el podcast'}
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
